import { Container, Graphics } from 'pixi.js';
import {
	FOOD_RADIUS,
	getWorldZone,
	getRandomFoodCategory,
	FeastTier,
	FEAST_TIER_CONFIGS,
	FEAST_SYSTEM,
	FoodType,
	FoodCategory,
	selectFoodTypeFromCategory,
	getFoodConfig,
	getFeastTierForValue,
} from '@wooorms/shared';
import type { FoodState } from '@wooorms/shared';
import { SpatialHashGrid } from '../utils/SpatialHashGrid';
import { EssenceType } from '../../constants';
import { Assets, Texture, Sprite, Rectangle } from 'pixi.js';

/**
 * Sprite sheet configuration
 */
const SPRITE_SHEET_PATH = '/assets/food/spritesheet.png';
const SPRITE_SIZE = 32; // Assuming 32x32 tiles

/**
 * Mapping of FoodType to sprite sheet grid coordinates [row, col]
 */
const FOOD_SPRITE_MAPPING: Record<string, [number, number]> = {
	// Junk Food (Row 0)
	[FoodType.CANDY]: [0, 0],
	[FoodType.LOLLIPOP]: [0, 1],
	[FoodType.COOKIE]: [0, 2],
	[FoodType.SODA]: [0, 3],
	[FoodType.DONUT]: [0, 4],
	[FoodType.FRIES]: [0, 5],

	// Junk High (Row 1)
	[FoodType.CHOCOLATE]: [1, 0],
	[FoodType.PIZZA]: [1, 1],
	[FoodType.HOT_DOG]: [1, 2],
	[FoodType.TACO]: [1, 3],
	[FoodType.BURGER]: [1, 4],
	[FoodType.POPCORN]: [1, 5],

	// Regular Food (Row 2)
	[FoodType.EGG]: [2, 0],
	[FoodType.BREAD]: [2, 1],
	[FoodType.CHEESE]: [2, 2],
	[FoodType.CHICKEN]: [2, 3],
	[FoodType.RICE]: [2, 4],
	[FoodType.RAMEN]: [2, 5],

	// Healthy Food (Row 3)
	[FoodType.APPLE]: [3, 0],
	[FoodType.CARROT]: [3, 1],
	[FoodType.GRAPES]: [3, 2],
	[FoodType.BROCCOLI]: [3, 3],
	[FoodType.STRAWBERRY]: [3, 4],
	[FoodType.SUSHI_ROLL]: [3, 5],
	[FoodType.AVOCADO]: [3, 5], // Fallback to Sushi for now as it was missing in generation
	[FoodType.STEAK]: [2, 3], // Fallback to Chicken or reuse
};

/**
 * Essence sprite mapping
 */
const ESSENCE_SPRITE_MAPPING: Record<string, [number, number]> = {
	[EssenceType.SMALL]: [5, 4],
	[EssenceType.MEDIUM]: [5, 5],
	[EssenceType.LARGE]: [5, 5], // Reuse Medium for now
};

/**
 * Feast sprite mapping [row, col]
 */
const FEAST_SPRITE_MAPPING: Record<string, [number, number]> = {
	[FeastTier.MEAL]: [4, 1], // Burger Meal
	[FeastTier.DINNER]: [4, 3], // Bento Box
	[FeastTier.FEAST]: [5, 0], // Cake
	[FeastTier.BANQUET]: [5, 1], // Champagne
	[FeastTier.GOLDEN_BUFFET]: [5, 2], // Golden Table
	[FeastTier.MYTHICAL_FEAST]: [5, 2], // Golden Table
	[FeastTier.AMVROSIA]: [5, 3], // Golden Jar
};

/**
 * Food item for rendering
 */
export interface FoodItem {
	id: string;
	x: number;
	y: number;
	radius: number;
	color: number;
	/** Food type (candy, apple, etc.) for health-themed food system */
	foodType?: FoodType;
	/** Food category (junk, regular, healthy) */
	foodCategory?: FoodCategory;
	/** Optional essence type for death drops */
	essenceType?: EssenceType;
	/** Optional value for scoring (used by essence) */
	value?: number;
	/** Feast tier for merged food (defaults to NONE for regular food) */
	feastTier?: FeastTier;
	/** Whether this is a fresh sprint drop (slightly transparent) */
	isFreshDrop?: boolean;
	/** Timestamp when this food was dropped (for fresh drop immunity) */
	droppedAt?: number;
}

/**
 * Food renderer configuration
 */
export interface FoodRendererConfig {
	/** Default food radius */
	defaultRadius: number;
	/** Glow intensity (0 = no glow) */
	glowIntensity: number;
	/** Animation speed multiplier */
	animationSpeed: number;
	/** Whether to show pulse animation */
	enablePulse: boolean;
}

/**
 * Default configuration
 */
const DEFAULT_CONFIG: FoodRendererConfig = {
	defaultRadius: FOOD_RADIUS,
	glowIntensity: 0.4,
	animationSpeed: 1,
	enablePulse: true,
};

/**
 * FoodRenderer - Efficiently renders multiple food items
 *
 * Uses a single Graphics object with batch drawing for performance.
 * PixiJS 8 batches graphics draw calls efficiently when using
 * a single Graphics object.
 *
 * Uses dirty flag system to avoid unnecessary redraws when food state
 * hasn't changed.
 */
export class FoodRenderer {
	/** Main container for food items */
	public container: Container;

	/** Container for food sprites */
	private spriteContainer: Container;

	/** Configuration */
	private config: FoodRendererConfig;

	/** Graphics object for batch rendering (glows/shadows) */
	private graphics: Graphics;

	/** Glow graphics (rendered below main food) */
	private glowGraphics: Graphics;

	/** Current food items */
	private foods: Map<string, FoodItem> = new Map();

	/** Sprite instances for food items */
	private sprites: Map<string, Sprite> = new Map();

	/** Loaded sprite sheet texture */
	private texture: Texture | null = null;

	/** Spatial hashGrid for fast collision queries (O(1) vs O(n)) */
	private spatialGrid: SpatialHashGrid<FoodItem>;

	/** Animation time accumulator */
	private animTime: number = 0;

	/** Visibility bounds for culling */
	private visibleBounds: { left: number; right: number; top: number; bottom: number } | null = null;

	/** Dirty flag - true when food state has changed and needs redraw */
	private dirty: boolean = true;

	/** Frame counter for animation update throttling */
	private frameCount: number = 0;

	/** How often to update animations (every N frames) - reduces redraw frequency */
	private readonly ANIMATION_UPDATE_INTERVAL: number = 2;

	constructor(config: Partial<FoodRendererConfig> = {}) {
		this.config = { ...DEFAULT_CONFIG, ...config };

		// Create container
		this.container = new Container();
		this.container.label = 'food-renderer';
		// Performance: Disable interactive events (we don't need click/hover on food)
		this.container.eventMode = 'none';
		this.container.interactiveChildren = false;

		// Create sprite container
		this.spriteContainer = new Container();
		this.spriteContainer.label = 'food-sprites';

		// Create graphics objects
		this.glowGraphics = new Graphics();
		this.graphics = new Graphics();

		// Initialize spatial hash grid with 100 unit cells
		// Cell size chosen to balance query speed vs cell count
		this.spatialGrid = new SpatialHashGrid<FoodItem>(100);

		// Add to container (order: glow -> shadows/highlights (graphics) -> sprites)
		this.container.addChild(this.glowGraphics);
		this.container.addChild(this.graphics); // Used for shadows/highlights
		this.container.addChild(this.spriteContainer); // Sprites on top

		// Load assets
		this.loadAssets();
	}

	/**
	 * Load sprite sheet texture
	 */
	private async loadAssets(): Promise<void> {
		try {
			this.texture = await Assets.load(SPRITE_SHEET_PATH);
			this.dirty = true;
		} catch (e) {
			console.error('Failed to load food sprites:', e);
		}
	}

	/**
	 * Set visible bounds for culling (in world coordinates)
	 * Marks dirty if bounds changed significantly (may affect which food is visible)
	 */
	setVisibleBounds(left: number, right: number, top: number, bottom: number): void {
		// Check if bounds changed significantly (more than 50 pixels)
		if (this.visibleBounds) {
			const threshold = 50;
			const changed =
				Math.abs(this.visibleBounds.left - left) > threshold ||
				Math.abs(this.visibleBounds.right - right) > threshold ||
				Math.abs(this.visibleBounds.top - top) > threshold ||
				Math.abs(this.visibleBounds.bottom - bottom) > threshold;
			if (changed) {
				this.dirty = true;
			}
		} else {
			this.dirty = true;
		}
		this.visibleBounds = { left, right, top, bottom };
	}

	/**
	 * Clear visible bounds (render all food)
	 */
	clearVisibleBounds(): void {
		if (this.visibleBounds !== null) {
			this.dirty = true;
		}
		this.visibleBounds = null;
	}

	/**
	 * Update food items from server state
	 */
	updateFromState(foods: Map<string, FoodState>): void {
		// Identify removed foods
		const currentIds = new Set(this.foods.keys());
		const newIds = new Set(foods.keys());

		// Remove missing foods
		for (const id of currentIds) {
			if (!newIds.has(id)) {
				this.removeFood(id);
			}
		}

		// Add or update foods
		foods.forEach((food, id) => {
			const existing = this.foods.get(id);
			if (existing) {
				// Update existing
				existing.x = food.x;
				existing.y = food.y;
				existing.radius = food.radius;
				existing.color = food.color;
				existing.feastTier = food.feastTier as FeastTier; // Ensure update
				this.spatialGrid.update(existing);
			} else {
				// Add new
				const foodItem: FoodItem = {
					id: food.id,
					x: food.x,
					y: food.y,
					radius: food.radius,
					color: food.color,
					value: food.value,
				};
				if (food.foodType) foodItem.foodType = food.foodType as FoodType;
				if (food.foodCategory) foodItem.foodCategory = food.foodCategory as FoodCategory;
				if (food.feastTier) foodItem.feastTier = food.feastTier as FeastTier;

				this.addFood(foodItem);
			}
		});

		this.dirty = true;
	}

	/**
	 * Add a single food item
	 */
	addFood(food: FoodItem): void {
		const foodItem = { ...food };
		this.foods.set(food.id, foodItem);
		this.spatialGrid.insert(foodItem);

		// Create sprite if texture is loaded
		if (this.texture) {
			this.createFoodSprite(foodItem);
		}

		this.dirty = true;
	}

	/**
	 * Remove a food item by ID
	 */
	removeFood(id: string): void {
		this.foods.delete(id);
		this.spatialGrid.remove(id);

		// Remove sprite
		const sprite = this.sprites.get(id);
		if (sprite) {
			sprite.destroy();
			this.sprites.delete(id);
		}

		this.dirty = true;
	}

	/**
	 * Create a sprite for a food item
	 * All sprites use the same fixed size for visual consistency
	 */
	private createFoodSprite(food: FoodItem): void {
		if (!this.texture) return;

		// Fixed visual size for all food items
		const FIXED_FOOD_SIZE = 36;

		// Determine grid coordinates
		let row = 0;
		let col = 0;

		if (food.feastTier && food.feastTier !== FeastTier.NONE) {
			const feastMapping = FEAST_SPRITE_MAPPING[food.feastTier];
			if (feastMapping) [row, col] = feastMapping;
		} else if (food.essenceType) {
			const mapping = ESSENCE_SPRITE_MAPPING[food.essenceType];
			if (mapping) [row, col] = mapping;
			else[row, col] = [5, 4]; // Default essence
		} else if (food.foodType) {
			const mapping = FOOD_SPRITE_MAPPING[food.foodType];
			if (mapping) {
				[row, col] = mapping;
			}
		}
		// row and col default to [0,0] if no mapping found (Candy fallback)

		// Create texture frame
		const frame = new Rectangle(
			col * SPRITE_SIZE,
			row * SPRITE_SIZE,
			SPRITE_SIZE,
			SPRITE_SIZE
		);

		// Create sprite with framed texture (PixiJS 8 syntax)
		const texture = new Texture({ source: this.texture.source, frame });
		const sprite = new Sprite(texture);

		sprite.anchor.set(0.5);
		sprite.width = FIXED_FOOD_SIZE;
		sprite.height = FIXED_FOOD_SIZE;
		sprite.x = food.x;
		sprite.y = food.y;

		this.spriteContainer.addChild(sprite);
		this.sprites.set(food.id, sprite);
	}

	/**
	 * Update a food item position
	 */
	updateFoodPosition(id: string, x: number, y: number): void {
		const food = this.foods.get(id);
		if (food) {
			food.x = x;
			food.y = y;
			// Update spatial grid with new position
			this.spatialGrid.update(food);
		}
	}

	/**
	 * Get food item by ID
	 */
	getFood(id: string): FoodItem | undefined {
		return this.foods.get(id);
	}

	/**
	 * Get all food items
	 */
	getAllFood(): FoodItem[] {
		return Array.from(this.foods.values());
	}

	/**
	 * Get food count
	 */
	getFoodCount(): number {
		return this.foods.size;
	}

	/**
	 * Check if a food item is visible
	 */
	private isVisible(x: number, y: number, radius: number): boolean {
		if (!this.visibleBounds) return true;

		const { left, right, top, bottom } = this.visibleBounds;
		return (
			x + radius >= left &&
			x - radius <= right &&
			y + radius >= top &&
			y - radius <= bottom
		);
	}

	/**
	 * Update animation and redraw
	 * Uses dirty flag and animation throttling to reduce unnecessary redraws
	 * @param dt Delta time in seconds
	 */
	update(dt: number): void {
		this.animTime += dt * this.config.animationSpeed;
		this.frameCount++;

		// Redraw if:
		// 1. Food state has changed (dirty flag)
		// 2. Animation update interval reached (for floating/sparkle effects)
		const shouldRedraw = this.dirty || (this.frameCount % this.ANIMATION_UPDATE_INTERVAL === 0);

		if (shouldRedraw) {
			this.draw();
			this.dirty = false;
		}
	}

	/**
	 * Draw all food items - simplified for performance
	 * Just draws the food icon with minimal effects
	 */
	private draw(): void {
		const g = this.graphics;
		const glow = this.glowGraphics;

		g.clear();
		glow.clear();

		// Check if texture is loaded but sprites not created (e.g. loaded after food added)
		if (this.texture && this.sprites.size < this.foods.size) {
			this.foods.forEach((food) => {
				if (!this.sprites.has(food.id)) {
					this.createFoodSprite(food);
				}
			});
		}

		// Fixed visual size for all food items
		const FIXED_FOOD_SIZE = 36; // Same as r * 2 from fallback icons (r = 18)

		// Draw each food item
		this.foods.forEach((food) => {
			// Culling check
			const isVisible = this.isVisible(food.x, food.y, FIXED_FOOD_SIZE);

			// Update sprite visibility
			const sprite = this.sprites.get(food.id);
			if (sprite) {
				sprite.visible = isVisible;
			}

			if (!isVisible) {
				return;
			}

			// Update Sprite position if using sprites (all same size)
			if (sprite) {
				sprite.x = food.x;
				sprite.y = food.y;
				sprite.width = FIXED_FOOD_SIZE;
				sprite.height = FIXED_FOOD_SIZE;
			}

			// Draw fallback food icon (when sprite not loaded)
			if (!sprite) {
				this.drawFallbackFoodIcon(g, food.x, food.y, food.radius, food.color, food.color, food.foodType);
			}
		});
	}

	/**
	 * Draw fallback food icon when sprite sheet is not loaded
	 * All food items use the same fixed size for visual consistency
	 */
	private drawFallbackFoodIcon(
		g: Graphics,
		cx: number,
		cy: number,
		_radius: number, // Ignored - using fixed size for consistency
		color: number,
		_lighterColor: number, // Reserved for future use
		foodType?: FoodType
	): void {
		// FIXED size for ALL food icons (consistency)
		const r = 18;

		// Draw dark backdrop for contrast
		g.circle(cx, cy, r * 0.85);
		g.fill({ color: 0x000000, alpha: 0.4 });

		// Default colored circle as base
		g.circle(cx, cy, r * 0.75);
		g.fill(color);

		// Draw specific icon based on food type
		switch (foodType) {
			// === JUNK FOOD ===
			case FoodType.CANDY:
				// Wrapped candy shape
				this.drawCandyIcon(g, cx, cy, r);
				break;
			case FoodType.LOLLIPOP:
				// Spiral lollipop
				this.drawLollipopIcon(g, cx, cy, r);
				break;
			case FoodType.COOKIE:
				// Circle with chocolate chips
				this.drawCookieIcon(g, cx, cy, r);
				break;
			case FoodType.SODA:
				// Can shape
				this.drawSodaIcon(g, cx, cy, r);
				break;
			case FoodType.DONUT:
				// Ring shape with frosting
				this.drawDonutIcon(g, cx, cy, r);
				break;
			case FoodType.CHOCOLATE:
				// Chocolate bar
				this.drawChocolateIcon(g, cx, cy, r);
				break;
			case FoodType.FRIES:
				// French fries
				this.drawFriesIcon(g, cx, cy, r);
				break;
			case FoodType.PIZZA:
				// Pizza slice
				this.drawPizzaIcon(g, cx, cy, r);
				break;
			case FoodType.HOT_DOG:
				// Hot dog
				this.drawHotDogIcon(g, cx, cy, r);
				break;

			// === REGULAR FOOD ===
			case FoodType.EGG:
				// Fried egg
				this.drawEggIcon(g, cx, cy, r);
				break;
			case FoodType.BREAD:
				// Bread loaf
				this.drawBreadIcon(g, cx, cy, r);
				break;
			case FoodType.CHEESE:
				// Cheese wedge
				this.drawCheeseIcon(g, cx, cy, r);
				break;
			case FoodType.CHICKEN:
				// Drumstick
				this.drawChickenIcon(g, cx, cy, r);
				break;
			case FoodType.RICE:
				// Rice bowl
				this.drawRiceIcon(g, cx, cy, r);
				break;

			// === HEALTHY FOOD ===
			case FoodType.APPLE:
				// Apple with leaf
				this.drawAppleIcon(g, cx, cy, r);
				break;
			case FoodType.CARROT:
				// Carrot
				this.drawCarrotIcon(g, cx, cy, r);
				break;
			case FoodType.GRAPES:
				// Grape cluster
				this.drawGrapesIcon(g, cx, cy, r);
				break;
			case FoodType.BROCCOLI:
				// Broccoli tree
				this.drawBroccoliIcon(g, cx, cy, r);
				break;
			case FoodType.STRAWBERRY:
				// Strawberry
				this.drawStrawberryIcon(g, cx, cy, r);
				break;
			case FoodType.AVOCADO:
				// Avocado
				this.drawAvocadoIcon(g, cx, cy, r);
				break;

			default:
				// Just the base circle with highlight
				g.circle(cx - r * 0.2, cy - r * 0.2, r * 0.25);
				g.fill({ color: 0xffffff, alpha: 0.6 });
		}
	}

	// === SIMPLIFIED FOOD ICONS (Performance optimized) ===
	// Using only fill operations to avoid stroke path issues

	private drawCandyIcon(g: Graphics, cx: number, cy: number, r: number): void {
		// Main body
		g.roundRect(cx - r * 0.35, cy - r * 0.2, r * 0.7, r * 0.4, 3);
		g.fill(0xff69b4);
		// Wrapper ends
		g.circle(cx - r * 0.45, cy, r * 0.15);
		g.fill(0xff1493);
		g.circle(cx + r * 0.45, cy, r * 0.15);
		g.fill(0xff1493);
		// Highlight
		g.circle(cx - r * 0.1, cy - r * 0.05, r * 0.1);
		g.fill({ color: 0xffffff, alpha: 0.5 });
	}

	private drawLollipopIcon(g: Graphics, cx: number, cy: number, r: number): void {
		// Stick
		g.rect(cx - r * 0.05, cy + r * 0.1, r * 0.1, r * 0.45);
		g.fill(0xdeb887);
		// Candy top
		g.circle(cx, cy - r * 0.1, r * 0.4);
		g.fill(0xff6b6b);
		// Inner swirl (simplified)
		g.circle(cx, cy - r * 0.1, r * 0.25);
		g.fill(0xffffff);
		g.circle(cx, cy - r * 0.1, r * 0.12);
		g.fill(0xff6b6b);
	}

	private drawCookieIcon(g: Graphics, cx: number, cy: number, r: number): void {
		// Cookie base
		g.circle(cx, cy, r * 0.45);
		g.fill(0xd2691e);
		// Chocolate chips
		g.circle(cx - r * 0.18, cy - r * 0.12, r * 0.08);
		g.fill(0x4a3728);
		g.circle(cx + r * 0.12, cy - r * 0.08, r * 0.07);
		g.fill(0x4a3728);
		g.circle(cx, cy + r * 0.15, r * 0.08);
		g.fill(0x4a3728);
	}

	private drawSodaIcon(g: Graphics, cx: number, cy: number, r: number): void {
		// Can body
		g.roundRect(cx - r * 0.3, cy - r * 0.4, r * 0.6, r * 0.8, 4);
		g.fill(0xff0000);
		// Label stripe
		g.rect(cx - r * 0.3, cy - r * 0.08, r * 0.6, r * 0.2);
		g.fill(0xffffff);
		// Top rim
		g.ellipse(cx, cy - r * 0.35, r * 0.22, r * 0.07);
		g.fill(0xc0c0c0);
	}

	private drawDonutIcon(g: Graphics, cx: number, cy: number, r: number): void {
		// Outer ring
		g.circle(cx, cy, r * 0.45);
		g.fill(0xffc0cb);
		// Inner hole (dark)
		g.circle(cx, cy, r * 0.15);
		g.fill(0x222222);
		// Sprinkles
		g.circle(cx - r * 0.25, cy - r * 0.15, r * 0.05);
		g.fill(0x00ff00);
		g.circle(cx + r * 0.2, cy - r * 0.2, r * 0.05);
		g.fill(0xffff00);
		g.circle(cx + r * 0.15, cy + r * 0.2, r * 0.05);
		g.fill(0x00ffff);
	}

	private drawChocolateIcon(g: Graphics, cx: number, cy: number, r: number): void {
		// Bar shape
		g.roundRect(cx - r * 0.4, cy - r * 0.3, r * 0.8, r * 0.6, 3);
		g.fill(0x8b4513);
		// Squares (dark lines)
		g.rect(cx - r * 0.02, cy - r * 0.3, r * 0.04, r * 0.6);
		g.fill(0x5c2e0e);
		g.rect(cx - r * 0.4, cy - r * 0.02, r * 0.8, r * 0.04);
		g.fill(0x5c2e0e);
	}

	private drawFriesIcon(g: Graphics, cx: number, cy: number, r: number): void {
		// Fries (yellow rectangles)
		g.roundRect(cx - r * 0.22, cy - r * 0.45, r * 0.1, r * 0.5, 2);
		g.fill(0xffd700);
		g.roundRect(cx - r * 0.06, cy - r * 0.5, r * 0.1, r * 0.55, 2);
		g.fill(0xffcc00);
		g.roundRect(cx + r * 0.1, cy - r * 0.4, r * 0.1, r * 0.45, 2);
		g.fill(0xffd700);
		// Red container
		g.roundRect(cx - r * 0.35, cy + r * 0.05, r * 0.7, r * 0.35, 3);
		g.fill(0xff0000);
	}

	private drawPizzaIcon(g: Graphics, cx: number, cy: number, r: number): void {
		// Pizza slice (triangle)
		g.moveTo(cx, cy - r * 0.45);
		g.lineTo(cx - r * 0.4, cy + r * 0.35);
		g.lineTo(cx + r * 0.4, cy + r * 0.35);
		g.closePath();
		g.fill(0xffa500);
		// Crust edge
		g.roundRect(cx - r * 0.4, cy + r * 0.25, r * 0.8, r * 0.15, 3);
		g.fill(0xdaa520);
		// Pepperoni
		g.circle(cx - r * 0.12, cy, r * 0.1);
		g.fill(0x8b0000);
		g.circle(cx + r * 0.1, cy + r * 0.12, r * 0.08);
		g.fill(0x8b0000);
	}

	private drawHotDogIcon(g: Graphics, cx: number, cy: number, r: number): void {
		// Bun
		g.roundRect(cx - r * 0.45, cy - r * 0.18, r * 0.9, r * 0.4, 8);
		g.fill(0xdaa520);
		// Hot dog
		g.roundRect(cx - r * 0.4, cy - r * 0.08, r * 0.8, r * 0.2, 5);
		g.fill(0xcd5c5c);
		// Mustard line
		g.rect(cx - r * 0.35, cy - r * 0.02, r * 0.7, r * 0.04);
		g.fill(0xffff00);
	}

	// === REGULAR FOOD ICONS ===

	private drawEggIcon(g: Graphics, cx: number, cy: number, r: number): void {
		// Egg white
		g.ellipse(cx, cy, r * 0.5, r * 0.4);
		g.fill(0xffffff);
		// Yolk
		g.circle(cx, cy, r * 0.2);
		g.fill(0xffa500);
		// Highlight
		g.circle(cx - r * 0.06, cy - r * 0.06, r * 0.06);
		g.fill({ color: 0xffffff, alpha: 0.5 });
	}

	private drawBreadIcon(g: Graphics, cx: number, cy: number, r: number): void {
		// Bread loaf
		g.ellipse(cx, cy - r * 0.1, r * 0.45, r * 0.35);
		g.fill(0xdeb887);
		// Bottom flat
		g.rect(cx - r * 0.4, cy, r * 0.8, r * 0.25);
		g.fill(0xd2a06a);
		// Score mark
		g.ellipse(cx, cy - r * 0.15, r * 0.2, r * 0.08);
		g.fill(0xc4986a);
	}

	private drawCheeseIcon(g: Graphics, cx: number, cy: number, r: number): void {
		// Cheese wedge
		g.moveTo(cx - r * 0.4, cy + r * 0.3);
		g.lineTo(cx + r * 0.4, cy + r * 0.3);
		g.lineTo(cx + r * 0.25, cy - r * 0.35);
		g.closePath();
		g.fill(0xffd700);
		// Holes
		g.circle(cx - r * 0.08, cy + r * 0.08, r * 0.1);
		g.fill(0xffb700);
		g.circle(cx + r * 0.12, cy - r * 0.05, r * 0.07);
		g.fill(0xffb700);
	}

	private drawChickenIcon(g: Graphics, cx: number, cy: number, r: number): void {
		// Bone
		g.rect(cx + r * 0.1, cy - r * 0.4, r * 0.12, r * 0.3);
		g.fill(0xffefd5);
		g.circle(cx + r * 0.16, cy - r * 0.4, r * 0.08);
		g.fill(0xffefd5);
		// Meat
		g.ellipse(cx - r * 0.08, cy + r * 0.1, r * 0.38, r * 0.32);
		g.fill(0xdaa520);
		// Highlight
		g.circle(cx - r * 0.2, cy, r * 0.08);
		g.fill({ color: 0xffffff, alpha: 0.4 });
	}

	private drawRiceIcon(g: Graphics, cx: number, cy: number, r: number): void {
		// Bowl
		g.ellipse(cx, cy + r * 0.15, r * 0.4, r * 0.3);
		g.fill(0x4169e1);
		// Rice pile
		g.ellipse(cx, cy - r * 0.08, r * 0.35, r * 0.22);
		g.fill(0xffffff);
		// Rice grains
		g.circle(cx - r * 0.12, cy - r * 0.12, r * 0.05);
		g.fill(0xf0f0f0);
		g.circle(cx + r * 0.08, cy - r * 0.08, r * 0.05);
		g.fill(0xf0f0f0);
	}

	// === HEALTHY FOOD ICONS ===

	private drawAppleIcon(g: Graphics, cx: number, cy: number, r: number): void {
		// Apple body
		g.circle(cx, cy + r * 0.05, r * 0.42);
		g.fill(0xff0000);
		// Stem
		g.rect(cx - r * 0.03, cy - r * 0.45, r * 0.06, r * 0.15);
		g.fill(0x8b4513);
		// Leaf
		g.ellipse(cx + r * 0.12, cy - r * 0.4, r * 0.1, r * 0.06);
		g.fill(0x228b22);
		// Highlight
		g.circle(cx - r * 0.12, cy - r * 0.05, r * 0.1);
		g.fill({ color: 0xffffff, alpha: 0.4 });
	}

	private drawCarrotIcon(g: Graphics, cx: number, cy: number, r: number): void {
		// Carrot body
		g.moveTo(cx - r * 0.22, cy - r * 0.3);
		g.lineTo(cx + r * 0.22, cy - r * 0.3);
		g.lineTo(cx, cy + r * 0.45);
		g.closePath();
		g.fill(0xff8c00);
		// Greens (circles instead of strokes)
		g.ellipse(cx - r * 0.12, cy - r * 0.45, r * 0.06, r * 0.15);
		g.fill(0x228b22);
		g.ellipse(cx, cy - r * 0.5, r * 0.06, r * 0.18);
		g.fill(0x228b22);
		g.ellipse(cx + r * 0.12, cy - r * 0.45, r * 0.06, r * 0.15);
		g.fill(0x228b22);
	}

	private drawGrapesIcon(g: Graphics, cx: number, cy: number, r: number): void {
		// Grape cluster (just circles)
		const s = r * 0.16;
		g.circle(cx, cy + r * 0.2, s);
		g.fill(0x8b008b);
		g.circle(cx - s * 1.2, cy + r * 0.05, s);
		g.fill(0x9932cc);
		g.circle(cx + s * 1.2, cy + r * 0.05, s);
		g.fill(0x9932cc);
		g.circle(cx - s * 0.6, cy - r * 0.15, s);
		g.fill(0xba55d3);
		g.circle(cx + s * 0.6, cy - r * 0.15, s);
		g.fill(0xba55d3);
		g.circle(cx, cy - r * 0.3, s);
		g.fill(0xda70d6);
	}

	private drawBroccoliIcon(g: Graphics, cx: number, cy: number, r: number): void {
		// Stem
		g.rect(cx - r * 0.08, cy + r * 0.1, r * 0.16, r * 0.35);
		g.fill(0x228b22);
		// Florets
		g.circle(cx - r * 0.2, cy - r * 0.05, r * 0.18);
		g.fill(0x2e8b2e);
		g.circle(cx + r * 0.2, cy - r * 0.05, r * 0.18);
		g.fill(0x2e8b2e);
		g.circle(cx, cy - r * 0.2, r * 0.22);
		g.fill(0x32cd32);
	}

	private drawStrawberryIcon(g: Graphics, cx: number, cy: number, r: number): void {
		// Strawberry body (ellipse instead of complex path)
		g.ellipse(cx, cy + r * 0.05, r * 0.35, r * 0.42);
		g.fill(0xff0000);
		// Seeds
		g.circle(cx - r * 0.12, cy, r * 0.04);
		g.fill(0xffff00);
		g.circle(cx + r * 0.12, cy, r * 0.04);
		g.fill(0xffff00);
		g.circle(cx, cy + r * 0.18, r * 0.04);
		g.fill(0xffff00);
		// Green top
		g.ellipse(cx - r * 0.12, cy - r * 0.35, r * 0.08, r * 0.12);
		g.fill(0x228b22);
		g.ellipse(cx + r * 0.12, cy - r * 0.35, r * 0.08, r * 0.12);
		g.fill(0x228b22);
		g.ellipse(cx, cy - r * 0.38, r * 0.06, r * 0.1);
		g.fill(0x228b22);
	}

	private drawAvocadoIcon(g: Graphics, cx: number, cy: number, r: number): void {
		// Outer (skin)
		g.ellipse(cx, cy, r * 0.38, r * 0.45);
		g.fill(0x556b2f);
		// Inner (flesh)
		g.ellipse(cx, cy, r * 0.3, r * 0.38);
		g.fill(0x9acd32);
		// Pit
		g.circle(cx, cy + r * 0.08, r * 0.16);
		g.fill(0x8b4513);
	}

	/**
	 * Force redraw all food
	 */
	redraw(): void {
		this.draw();
	}

	/**
	 * Clear all food items
	 */
	clear(): void {
		this.foods.clear();
		this.spatialGrid.clear();
		this.graphics.clear();
		this.glowGraphics.clear();
	}

	/**
	 * Get food near a position (for collision detection)
	 * Uses spatial hash grid for O(1) lookup instead of O(n) linear scan
	 * Performance: 70-90% faster with 1000+ food items
	 */
	getFoodNear(x: number, y: number, radius: number): FoodItem[] {
		// Use spatial hash grid for fast query
		return this.spatialGrid.queryRadius(x, y, radius);
	}

	/**
	 * Merge nearby essence/food into feasts when they cluster together
	 * This creates exciting high-value targets from death drops!
	 *
	 * The merge process:
	 * 1. Find clusters of essence within merge radius
	 * 2. Calculate total value of cluster
	 * 3. If value meets feast threshold, replace with single feast item
	 * 4. Feast tier is based on total accumulated value
	 *
	 * @param mergeRadius - Radius to search for nearby food to merge (default 50)
	 * @param minItemsToMerge - Minimum items needed to trigger merge (default 3)
	 */
	mergeNearbyEssence(mergeRadius: number = 50, minItemsToMerge: number = 3): void {
		const processedIds = new Set<string>();
		const foodToRemove: string[] = [];
		const feastsToAdd: FoodItem[] = [];

		// Convert to array for iteration while modifying
		const allFood = Array.from(this.foods.values());

		for (const food of allFood) {
			// Skip if already processed or not an essence drop
			if (processedIds.has(food.id) || !food.essenceType) {
				continue;
			}

			// Find nearby essence within merge radius
			const nearby = this.getFoodNear(food.x, food.y, mergeRadius)
				.filter(f => f.essenceType !== undefined && !processedIds.has(f.id));

			// Need minimum items to merge
			if (nearby.length < minItemsToMerge) {
				processedIds.add(food.id);
				continue;
			}

			// Calculate total value and center of mass
			let totalValue = 0;
			let centerX = 0;
			let centerY = 0;
			let maxRadius = 0;

			for (const item of nearby) {
				const itemValue = item.value ?? 1;
				totalValue += itemValue;
				centerX += item.x * itemValue;
				centerY += item.y * itemValue;
				maxRadius = Math.max(maxRadius, item.radius);
				processedIds.add(item.id);
				foodToRemove.push(item.id);
			}

			// Weighted center of mass
			centerX /= totalValue;
			centerY /= totalValue;

			// Determine feast tier based on total value
			const feastTier = getFeastTierForValue(totalValue);

			// Only create feast if we meet at least MEAL threshold
			if (feastTier === FeastTier.NONE) {
				// Not enough value for a feast, just mark as processed
				// Keep the original items - remove them from foodToRemove
				for (const item of nearby) {
					const idx = foodToRemove.indexOf(item.id);
					if (idx !== -1) {
						foodToRemove.splice(idx, 1);
					}
				}
				continue;
			}

			// Get feast config for visual properties
			const feastConfig = FEAST_TIER_CONFIGS[feastTier];

			// Create feast item at center of mass
			feastsToAdd.push({
				id: `feast-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
				x: centerX,
				y: centerY,
				radius: maxRadius * feastConfig.sizeMultiplier,
				color: feastConfig.color,
				value: totalValue,
				feastTier: feastTier,
			});
		}

		// Remove merged items from both foods map and spatial grid
		for (const id of foodToRemove) {
			this.foods.delete(id);
			this.spatialGrid.remove(id);
		}

		// Add feast items to both foods map and spatial grid
		for (const feast of feastsToAdd) {
			this.foods.set(feast.id, feast);
			this.spatialGrid.insert(feast);
		}

		// Mark dirty if we made changes
		if (foodToRemove.length > 0 || feastsToAdd.length > 0) {
			this.dirty = true;
		}
	}

	/**
	 * Merge nearby food items into feasts (periodic check for all food types)
	 *
	 * This method checks ALL food items (not just essence) for potential merging:
	 * - Sprint drops have a fresh drop immunity period (won't merge until aged)
	 * - Regular food and aged sprint drops can merge if clustered
	 * - Creates exciting high-value feast targets
	 *
	 * @param currentTime - Current game time in seconds (for fresh drop immunity)
	 * @returns Array of feast tiers created (for playing appropriate sounds)
	 */
	mergeAllFood(currentTime: number): FeastTier[] {
		const processedIds = new Set<string>();
		const foodToRemove: string[] = [];
		const feastsToAdd: FoodItem[] = [];

		// Convert to array for iteration while modifying
		const allFood = Array.from(this.foods.values());

		// Filter to only food that can merge (not fresh drops, not existing feasts)
		const mergeableFood = allFood.filter(food => {
			// Skip if already a feast (feasts don't merge further)
			if (food.feastTier && food.feastTier !== FeastTier.NONE) {
				return false;
			}

			// Check fresh drop immunity
			if (food.isFreshDrop && food.droppedAt !== undefined) {
				const age = currentTime - food.droppedAt;
				if (age < FEAST_SYSTEM.FRESH_DROP_IMMUNITY_TIME) {
					return false; // Still immune
				}
				// Aged past immunity - mark as no longer fresh for visual update
				food.isFreshDrop = false;
			}

			return true;
		});

		// Need enough food to even check for merges
		if (mergeableFood.length < FEAST_SYSTEM.MIN_FOOD_FOR_MERGE) {
			return [];
		}

		for (const food of mergeableFood) {
			// Skip if already processed
			if (processedIds.has(food.id)) {
				continue;
			}

			// Find nearby mergeable food within merge radius
			const nearby = mergeableFood.filter(f => {
				if (f.id === food.id) return false;
				if (processedIds.has(f.id)) return false;

				const dx = f.x - food.x;
				const dy = f.y - food.y;
				const distSq = dx * dx + dy * dy;
				const radiusSq = FEAST_SYSTEM.MERGE_RADIUS * FEAST_SYSTEM.MERGE_RADIUS;

				return distSq <= radiusSq;
			});

			// Need minimum items to merge (including center)
			if (nearby.length < FEAST_SYSTEM.MIN_FOOD_FOR_MERGE - 1) {
				processedIds.add(food.id);
				continue;
			}

			// Calculate total value and center of mass
			// Include the center food in calculations
			const allToMerge = [food, ...nearby];
			let totalValue = 0;
			let centerX = 0;
			let centerY = 0;
			let maxRadius = 0;

			for (const item of allToMerge) {
				const itemValue = item.value ?? 1;
				totalValue += itemValue;
				centerX += item.x * itemValue;
				centerY += item.y * itemValue;
				maxRadius = Math.max(maxRadius, item.radius);
				processedIds.add(item.id);
				foodToRemove.push(item.id);
			}

			// Weighted center of mass
			centerX /= totalValue;
			centerY /= totalValue;

			// Determine feast tier based on total value
			const feastTier = getFeastTierForValue(totalValue);

			// Only create feast if we meet at least MEAL threshold
			if (feastTier === FeastTier.NONE) {
				// Not enough value for a feast, keep the original items
				for (const item of allToMerge) {
					const idx = foodToRemove.indexOf(item.id);
					if (idx !== -1) {
						foodToRemove.splice(idx, 1);
					}
				}
				continue;
			}

			// Get feast config for visual properties
			const feastConfig = FEAST_TIER_CONFIGS[feastTier];

			// Create feast item at center of mass
			feastsToAdd.push({
				id: `feast-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
				x: centerX,
				y: centerY,
				radius: maxRadius * feastConfig.sizeMultiplier,
				color: feastConfig.color,
				value: totalValue,
				feastTier: feastTier,
			});
		}

		// Remove merged items from both foods map and spatial grid
		for (const id of foodToRemove) {
			this.foods.delete(id);
			this.spatialGrid.remove(id);
		}

		// Add feast items to both foods map and spatial grid
		for (const feast of feastsToAdd) {
			this.foods.set(feast.id, feast);
			this.spatialGrid.insert(feast);
		}

		// Mark dirty if we made changes
		if (foodToRemove.length > 0 || feastsToAdd.length > 0) {
			this.dirty = true;
		}

		// Return the tiers of all created feasts
		return feastsToAdd.map(f => f.feastTier ?? FeastTier.NONE).filter(t => t !== FeastTier.NONE);
	}

	/**
	 * Spawn test food items in a circular area with zone-based food distribution
	 *
	 * Zone-Based Food Distribution:
	 * - CORE zone (0-33%): 70% junk, 25% regular, 5% healthy - safe farming
	 * - MID zone (33-66%): 50% junk, 35% regular, 15% healthy - balanced play
	 * - RIM zone (66-100%): 30% junk, 40% regular, 30% healthy - high risk/reward
	 *
	 * @param centerX - Center X coordinate
	 * @param centerY - Center Y coordinate
	 * @param radius - Spawn radius (world radius)
	 * @param count - Number of food items to spawn
	 */
	spawnTestFood(centerX: number, centerY: number, radius: number, count: number): void {
		for (let i = 0; i < count; i++) {
			// Random position within circle
			const angle = Math.random() * Math.PI * 2;
			const r = Math.sqrt(Math.random()) * radius;
			const x = centerX + Math.cos(angle) * r;
			const y = centerY + Math.sin(angle) * r;

			// Determine zone based on position (using radius as world radius)
			const zone = getWorldZone(x, y, radius);

			// Get random food category based on zone spawn rates
			const category = getRandomFoodCategory(zone);

			// Select a specific food type from the category (health-themed food system)
			const selectedFoodType = selectFoodTypeFromCategory(category);
			const config = getFoodConfig(selectedFoodType);

			// Get color, value, and size from food type config
			const color = config.color;
			const value = config.value;
			const foodRadius = this.config.defaultRadius * config.sizeMultiplier;

			this.addFood({
				id: `test-food-${i}`,
				x,
				y,
				radius: foodRadius,
				color,
				value,
				foodType: selectedFoodType,
				foodCategory: category,
			});
		}
	}

	/**
	 * Clean up resources
	 */
	destroy(): void {
		this.graphics.destroy();
		this.glowGraphics.destroy();
		this.container.destroy({ children: true });
		this.foods.clear();
		this.spatialGrid.clear();
	}
}
